// Domain 06 — RouteZero Security Operations Center (SOC), Taguig (BGC).
//
//   OUTSIDE 198.51.100.77 (Internet user) ── ISP (unmanaged) ── G0/0 203.0.113.18/30 ─┐
//                                                                                     R1  edge / core router
//   USERS  10.60.10.0/24 (VLAN 10) ── G0/1 .1 ─ SW1 ─ PC-01 .11 (Fa0/1) · PC-02 .12 (Fa0/2) · CONTRACTOR .200 (Fa0/3)
//                                             SW1 management: Vlan10 10.60.10.2, default gateway .1
//   SERVERS 10.60.20.0/24 ── G0/2 .1 ─ SW2 ─ SRV-AAA .10 (RADIUS/TACACS+) · SRV-FILE .30 (SMB) · SRV-LOG .40 (syslog) · SRV-APP .80 (HTTPS)
//   MGMT    10.60.99.0/24 ── G0/3 .1 ─ ADMIN-PC .10 (the network team's jump host)
//
// No real names, addresses or credentials: documentation ranges, the fictional
// RouteZero Technologies, and passwords/keys that are obviously lab-only values.

import { pc, server, router, sw, link, network } from '../../engine/net/model.js';
import { createInterface } from '../../engine/ios/switching.js';
import { ensureMgmt, aaaOf, PSEC_DEFAULT } from '../../engine/net/security.js';
import { addAce } from '../../engine/ios/secCli.js';
import { stdPatterns, extPatterns } from '../../engine/ios/secCli.js';

const M = { 24: '255.255.255.0', 30: '255.255.255.252' };
const ipm = (s) => { const [ip, p] = s.split('/'); return { ip, mask: M[p] }; };

export const DOMAIN = 'taguig.routezero.lab';
/** Lab-only credentials (never real values). */
export const CRED = {
  weak: 'cisco',                 // what the previous contractor left behind
  enable: 'RzLab-Enable-26',     // enable secret
  console: 'RzLab-Console-26',   // console line password
  admin: { user: 'netadmin', pass: 'RzLab-Admin-26' },   // local fallback account (privilege 15)
  helpdesk: { user: 'helpdesk', pass: 'RzLab-Help-26' }, // local, privilege 1
  aaaUser: { user: 'j.reyes', pass: 'RzLab-Reyes-26' },   // network engineer in the AAA server (privilege 15)
  aaaUser2: { user: 'm.santos', pass: 'RzLab-Santos-26' }, // service-desk analyst in the AAA server (privilege 1)
  radiusKey: 'RzLab-Radius-Key', tacacsKey: 'RzLab-Tacacs-Key',
};
export const BANNER = 'Authorized access only. RouteZero Technologies - activity is logged.';
export const IP = {
  R1: { 'GigabitEthernet0/0': '203.0.113.18/30', 'GigabitEthernet0/1': '10.60.10.1/24', 'GigabitEthernet0/2': '10.60.20.1/24', 'GigabitEthernet0/3': '10.60.99.1/24' },
  ISP: { 'GigabitEthernet0/0': '203.0.113.17/30', 'GigabitEthernet0/1': '198.51.100.1/24' },
};
export const HOSTS = {
  'PC-01': { ip: '10.60.10.11', mac: '0050796a1011' }, 'PC-02': { ip: '10.60.10.12', mac: '0050796a1012' },
  CONTRACTOR: { ip: '10.60.10.200', mac: '0050796a10c8' }, 'ADMIN-PC': { ip: '10.60.99.10', mac: '0050796a9910' },
  'SRV-AAA': { ip: '10.60.20.10' }, 'SRV-FILE': { ip: '10.60.20.30' }, 'SRV-LOG': { ip: '10.60.20.40' }, 'SRV-APP': { ip: '10.60.20.80' },
  OUTSIDE: { ip: '198.51.100.77' },
};
/** A visitor's personal laptop (used by the port-security missions). */
export const ROGUE_MAC = '00e04c6a77ab';
const DESC = { 'GigabitEthernet0/0': 'ISP-INTERNET', 'GigabitEthernet0/1': 'USERS-VLAN10', 'GigabitEthernet0/2': 'SERVER-FARM', 'GigabitEthernet0/3': 'MGMT-JUMP' };

function mkR1() {
  const r = router('R1', { seq: 6, ports: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2', 'GigabitEthernet0/3'] });
  for (const [n, a] of Object.entries(IP.R1)) Object.assign(createInterface(r, n), { ip: ipm(a), shutdown: false, description: DESC[n] });
  r.routes.push({ net: '0.0.0.0', mask: '0.0.0.0', nh: '203.0.113.17' });
  return r;
}
function mkIsp() {
  const r = router('ISP', { seq: 9 });
  for (const [n, a] of Object.entries(IP.ISP)) Object.assign(createInterface(r, n), { ip: ipm(a), shutdown: false });
  r.managed = false;
  return r;
}
const svr = (id, services, extra = {}) => Object.assign(server(id, { ip: HOSTS[id].ip, mask: M[24], gw: '10.60.20.1', mac: `00155d6c20${HOSTS[id].ip.split('.')[3].padStart(2, '0')}`, services }), extra);

/** The AAA server's database (RADIUS + TACACS+). Clients are the NAS addresses it trusts. */
export function aaaServerDb() {
  return {
    radius: { enabled: true, clients: { '10.60.20.1': { name: 'R1', key: CRED.radiusKey } } },
    tacacs: { enabled: true, clients: { '10.60.20.1': { name: 'R1', key: CRED.tacacsKey } } },
    users: {
      [CRED.aaaUser.user]: { password: CRED.aaaUser.pass, priv: 15, allow: ['radius', 'tacacs'] },
      [CRED.aaaUser2.user]: { password: CRED.aaaUser2.pass, priv: 1, allow: ['radius', 'tacacs'] },
    },
    log: [],
  };
}

/**
 * The Taguig site as the previous contractor left it ("as found"): reachable,
 * routed, and wide open — Telnet from anywhere with the password "cisco",
 * plaintext enable password, no SSH, no ACLs, no port security, no AAA.
 */
export function soc() {
  const R1 = mkR1();
  const ISP = mkIsp();
  const SW1 = sw('SW1', { seq: 21, fastPorts: 8, vlans: { 10: { name: 'USERS' } } });
  const SW2 = sw('SW2', { seq: 22, fastPorts: 8 });
  for (const n of ['FastEthernet0/1', 'FastEthernet0/2', 'FastEthernet0/3', 'GigabitEthernet0/1']) Object.assign(SW1.interfaces[n], { mode: 'access', explicitAccess: true, accessVlan: 10 });
  SW1.interfaces['FastEthernet0/1'].description = 'PC-01 desk 4F-12';
  SW1.interfaces['FastEthernet0/2'].description = 'PC-02 desk 4F-14';
  SW1.interfaces['FastEthernet0/3'].description = 'CONTRACTOR hot-desk';
  SW1.interfaces['GigabitEthernet0/1'].description = 'UPLINK-R1';
  Object.assign(createInterface(SW1, 'Vlan10'), { ip: ipm('10.60.10.2/24'), shutdown: false });
  SW1.defaultGateway = '10.60.10.1';
  const devs = [R1, ISP, SW1, SW2,
    pc('PC-01', { ip: HOSTS['PC-01'].ip, mask: M[24], gw: '10.60.10.1', mac: HOSTS['PC-01'].mac }),
    pc('PC-02', { ip: HOSTS['PC-02'].ip, mask: M[24], gw: '10.60.10.1', mac: HOSTS['PC-02'].mac }),
    pc('CONTRACTOR', { ip: HOSTS.CONTRACTOR.ip, mask: M[24], gw: '10.60.10.1', mac: HOSTS.CONTRACTOR.mac, user: 'contractor' }),
    pc('ADMIN-PC', { ip: HOSTS['ADMIN-PC'].ip, mask: M[24], gw: '10.60.99.1', mac: HOSTS['ADMIN-PC'].mac, user: 'netadmin' }),
    pc('OUTSIDE', { ip: HOSTS.OUTSIDE.ip, mask: M[24], gw: '198.51.100.1', mac: '0050797f4d4d', user: 'guest' }),
    svr('SRV-AAA', [{ proto: 'udp', port: 1812, name: 'radius' }, { proto: 'udp', port: 1813, name: 'radius-acct' }, { proto: 'tcp', port: 49, name: 'tacacs' }], { aaa: aaaServerDb() }),
    svr('SRV-FILE', [{ proto: 'tcp', port: 445, name: 'smb' }]),
    svr('SRV-LOG', [{ proto: 'udp', port: 514, name: 'syslog' }]),
    svr('SRV-APP', [{ proto: 'tcp', port: 443, name: 'https' }, { proto: 'tcp', port: 80, name: 'http' }], { http: { title: 'RouteZero Timesheets', body: `apps.${DOMAIN}` } }),
  ];
  const links = [
    link('R1', 'GigabitEthernet0/0', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)' }),
    link('OUTSIDE', 'FastEthernet0', 'ISP', 'GigabitEthernet0/1'),
    link('SW1', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/1'),
    link('SW2', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/2'),
    link('ADMIN-PC', 'FastEthernet0', 'R1', 'GigabitEthernet0/3'),
    link('PC-01', 'FastEthernet0', 'SW1', 'FastEthernet0/1'), link('PC-02', 'FastEthernet0', 'SW1', 'FastEthernet0/2'), link('CONTRACTOR', 'FastEthernet0', 'SW1', 'FastEthernet0/3'),
    link('SRV-AAA', 'FastEthernet0', 'SW2', 'FastEthernet0/1'), link('SRV-FILE', 'FastEthernet0', 'SW2', 'FastEthernet0/2'),
    link('SRV-LOG', 'FastEthernet0', 'SW2', 'FastEthernet0/3'), link('SRV-APP', 'FastEthernet0', 'SW2', 'FastEthernet0/4'),
  ];
  const n = network(devs, links);
  asFound(n);
  return n;
}

// ------------------------------------------------------------ presets (the as-built designs)
/** Contractor defaults: Telnet with "cisco", plaintext enable password. */
export function asFound(n, ids = ['R1', 'SW1']) {
  for (const id of ids) {
    const m = ensureMgmt(n.devices[id]);
    m.enablePassword = { pw: CRED.weak, enc: false };
    m.lines.vty.password = CRED.weak;
    m.lines.vty.login = 'line';
  }
  return n;
}
/** Mission 02 design: secret, encryption, console login, banner, minimum length. */
export function hardenAccess(n, ids = ['R1', 'SW1']) {
  for (const id of ids) {
    const m = ensureMgmt(n.devices[id]);
    m.minLength = 10;
    m.enableSecret = { pw: CRED.enable, type: 5 };
    m.enablePassword = null;
    m.encrypt = true;
    Object.assign(m.lines.con, { password: CRED.console, login: 'line', execTimeout: [5, 0], logSync: true });
    m.lines.vty.execTimeout = [10, 0];
    m.banner = BANNER;
  }
  return n;
}
/** Mission 03 design: SSH v2 only with local accounts. */
export function sshDesign(n, ids = ['R1', 'SW1']) {
  for (const id of ids) {
    const d = n.devices[id];
    const m = ensureMgmt(d);
    m.domainName = DOMAIN;
    m.rsa = { bits: 2048, name: `${d.hostname}.${DOMAIN}` };
    m.ssh.version = 2;
    m.users[CRED.admin.user] = { pw: CRED.admin.pass, priv: 15, secret: true, type: 5 };
    Object.assign(m.lines.vty, { login: 'local', transport: ['ssh'], password: null });
  }
  return n;
}
/** Mission 04 design: only the management subnet may open a VTY session. */
export function mgmtAclDesign(n, ids = ['R1', 'SW1']) {
  for (const id of ids) {
    const d = n.devices[id];
    d.acls ||= {};
    d.acls['15'] = [];
    addAce(d, '15', { kind: 'std', action: 'permit', base: '10.60.99.0', wild: '0.0.0.255', log: false });
    addAce(d, '15', { kind: 'std', action: 'deny', base: '0.0.0.0', wild: '255.255.255.255', log: true });
    ensureMgmt(d).lines.vty.accessClass = '15';
  }
  return n;
}
/** Mission 06 design: contractors (10.60.10.192/26) may not reach the server farm. */
export function stdAclDesign(n) {
  const d = n.devices.R1;
  d.acls ||= {};
  d.acls['20'] = [];
  addAce(d, '20', { kind: 'std', action: 'deny', base: '10.60.10.192', wild: '0.0.0.63', log: false });
  addAce(d, '20', { kind: 'std', action: 'permit', base: '0.0.0.0', wild: '255.255.255.255', log: false });
  d.interfaces['GigabitEthernet0/2'].accessGroup = { in: null, out: '20' };
  return n;
}
const ANY = { base: '0.0.0.0', wild: '255.255.255.255' };
const USERS = { base: '10.60.10.0', wild: '0.0.0.255' };
const H = (ip) => ({ base: ip, wild: '0.0.0.0' });
/** Mission 07 design: named extended ACL USERS-IN inbound on G0/1. */
export function extAclDesign(n) {
  const d = n.devices.R1;
  d.acls ||= {}; d.aclType ||= {};
  d.acls['USERS-IN'] = [];
  d.aclType['USERS-IN'] = 'extended';
  const e = (o) => addAce(d, 'USERS-IN', { kind: 'ext', log: false, ...o });
  e({ action: 'permit', proto: 'tcp', src: H('10.60.10.2'), sport: { op: 'eq', a: 22 }, dst: { base: '10.60.99.0', wild: '0.0.0.255' } });
  e({ action: 'permit', proto: 'tcp', src: USERS, dst: H('10.60.20.80'), dport: { op: 'eq', a: 443 } });
  e({ action: 'permit', proto: 'tcp', src: USERS, dst: H('10.60.20.30'), dport: { op: 'eq', a: 445 } });
  e({ action: 'permit', proto: 'icmp', src: USERS, dst: H('10.60.20.80'), icmpType: 'echo' });
  e({ action: 'deny', proto: 'ip', src: USERS, dst: { base: '10.60.20.0', wild: '0.0.0.255' }, log: true });
  e({ action: 'deny', proto: 'ip', src: USERS, dst: { base: '10.60.99.0', wild: '0.0.0.255' }, log: true });
  e({ action: 'permit', proto: 'ip', src: ANY, dst: ANY });
  d.interfaces['GigabitEthernet0/1'].accessGroup = { ...(d.interfaces['GigabitEthernet0/1'].accessGroup || {}), in: 'USERS-IN' };
  return n;
}
/** Mission 10 design: sticky port security on the desk ports, unused ports parked. */
export function psecDesign(n) {
  const S = n.devices.SW1;
  S.vlans[999] = { name: 'PARKING' };
  for (const [port, host] of [['FastEthernet0/1', 'PC-01'], ['FastEthernet0/2', 'PC-02'], ['FastEthernet0/3', 'CONTRACTOR']]) {
    const i = S.interfaces[port];
    i.psec = { ...PSEC_DEFAULT(), enabled: true, sticky: true, violation: port === 'FastEthernet0/3' ? 'restrict' : 'shutdown', macs: [{ mac: macDot(HOSTS[host].mac), type: 'sticky', vlan: 10 }] };
  }
  for (const k of [4, 5, 6, 7, 8]) Object.assign(S.interfaces[`FastEthernet0/${k}`], { shutdown: true, accessVlan: 999, mode: 'access', explicitAccess: true });
  return n;
}
export const macDot = (m) => `${m.slice(0, 4)}.${m.slice(4, 8)}.${m.slice(8, 12)}`;
/** Mission 12 design: AAA — TACACS+ for VTY administration with local fallback, RADIUS server defined. */
export function aaaDesign(n) {
  const d = n.devices.R1;
  const a = aaaOf(d);
  a.newModel = true;
  a.authLogin = { default: ['local'], 'VTY-ADMIN': ['group tacacs+', 'local'] };
  a.acctExec = 'group tacacs+';
  a.servers.radius['RZ-RADIUS'] = { ip: '10.60.20.10', authPort: 1812, acctPort: 1813, key: CRED.radiusKey };
  a.servers.tacacs['RZ-TACACS'] = { ip: '10.60.20.10', key: CRED.tacacsKey };
  ensureMgmt(d).lines.vty.authList = 'VTY-ADMIN';
  return n;
}
/** Logging of management access to SRV-LOG. */
export function loggingDesign(n, ids = ['R1']) {
  for (const id of ids) {
    const d = n.devices[id];
    const m = ensureMgmt(d);
    m.loginLog = { failure: true, success: true };
    d.timestamps = true;
    d.logging = { hosts: ['10.60.20.40'], trap: 'informational', buffered: true };
  }
  return n;
}
/** Build the network with a list of presets applied in order. */
export function socWith(...steps) {
  const n = soc();
  for (const f of steps) f(n);
  return n;
}
export { stdPatterns, extPatterns };

// ------------------------------------------------------------ diagram
const NODE = {
  OUTSIDE: { x: 120, y: 60, type: 'pc', sub: 'Internet user 198.51.100.77' },
  ISP: { x: 430, y: 60, type: 'cloud', sub: 'ISP · unmanaged' },
  R1: { x: 430, y: 215, type: 'router', sub: 'Taguig edge/core' },
  'ADMIN-PC': { x: 780, y: 215, type: 'pc', sub: 'Jump host 10.60.99.10' },
  SW1: { x: 200, y: 360, type: 'switch', sub: 'Access · mgmt Vlan10 .2' },
  'PC-01': { x: 70, y: 500, type: 'pc', sub: '10.60.10.11 · Fa0/1' },
  'PC-02': { x: 200, y: 500, type: 'pc', sub: '10.60.10.12 · Fa0/2' },
  CONTRACTOR: { x: 330, y: 500, type: 'pc', sub: '10.60.10.200 · Fa0/3' },
  SW2: { x: 690, y: 360, type: 'switch', sub: 'Server farm' },
  'SRV-AAA': { x: 520, y: 500, type: 'server', sub: 'AAA .10 RADIUS/TACACS+' },
  'SRV-FILE': { x: 640, y: 500, type: 'server', sub: 'File .30 (SMB 445)' },
  'SRV-LOG': { x: 760, y: 500, type: 'server', sub: 'Syslog .40' },
  'SRV-APP': { x: 880, y: 500, type: 'server', sub: 'App .80 (HTTPS)' },
};
const LABELS = {
  'R1:GigabitEthernet0/0': 'G0/0 .18', 'ISP:GigabitEthernet0/0': '.17', 'R1:GigabitEthernet0/1': 'G0/1 .1', 'SW1:GigabitEthernet0/1': 'G0/1',
  'R1:GigabitEthernet0/2': 'G0/2 .1', 'SW2:GigabitEthernet0/1': 'G0/1', 'R1:GigabitEthernet0/3': 'G0/3 .1', 'ADMIN-PC:FastEthernet0': '.10',
  'SW1:FastEthernet0/1': 'Fa0/1', 'SW1:FastEthernet0/2': 'Fa0/2', 'SW1:FastEthernet0/3': 'Fa0/3',
  'SW2:FastEthernet0/1': 'Fa0/1', 'SW2:FastEthernet0/2': 'Fa0/2', 'SW2:FastEthernet0/3': 'Fa0/3', 'SW2:FastEthernet0/4': 'Fa0/4',
};
export function socLayout(n, { title, notes = {} } = {}) {
  return {
    width: 1000, height: 590, title: title || 'RouteZero SOC — Taguig', nodes: Object.fromEntries(Object.entries(NODE).filter(([id]) => n.devices[id])),
    zones: [
      { x: 20, y: 8, w: 560, h: 110, label: 'INTERNET · 198.51.100.0/24 · edge link 203.0.113.16/30', cloud: true },
      { x: 20, y: 300, w: 400, h: 270, label: 'USERS · VLAN 10 · 10.60.10.0/24 · gateway .1' },
      { x: 450, y: 300, w: 530, h: 270, label: 'SERVER FARM · 10.60.20.0/24 · gateway .1' },
      { x: 640, y: 150, w: 340, h: 120, label: 'MGMT · 10.60.99.0/24' },
    ],
    linkLabels: LABELS,
    linkNotes: { 'R1:GigabitEthernet0/0--ISP:GigabitEthernet0/0': '203.0.113.16/30', ...notes },
    overlays: [],
  };
}
